import { makeAutoObservable, runInAction } from 'mobx'
import { SendFromStore } from 'widgets/SendFrom'
import { EnrollmentFilters } from 'widgets/ContactEnrollment'
import { formatWidgetMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import type { TriggerSectionStore } from './triggerSectionStore'

export const STEP_HEADERS = [
  'How should this agent be triggered?',
  'Which inbox should the agent use to start conversations?',
  'How should contacts be enrolled?',
  'Allow contacts to re-enroll after completion?',
]

export const RE_ENROLL_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

export class TriggerWidgetStore {
  step = 1
  focusedIndex = 0
  isSkipped = false
  localRunAutomatically: boolean
  localEnrollmentFilters = EnrollmentFilters.Empty()
  readonly localSendFromStore: SendFromStore

  constructor(readonly sectionStore: TriggerSectionStore, readonly chatStore: ChatStore) {
    this.localRunAutomatically = sectionStore.runAutomatically
    this.localSendFromStore = new SendFromStore(sectionStore.entities, { includeRCS: true })
    this.localSendFromStore.init()
    makeAutoObservable(this)
  }

  get totalSteps() {
    return this.localRunAutomatically ? 4 : 2
  }

  get isLastStep() {
    return this.step === this.totalSteps
  }

  get currentListLength() {
    switch (this.step) {
      case 1:
        return 2
      case 4:
        return RE_ENROLL_OPTIONS.length
      default:
        return 0
    }
  }

  get safeIndex() {
    if (!this.currentListLength) return 0
    return Math.min(this.focusedIndex, this.currentListLength - 1)
  }

  get isSubmitDisabled() {
    return this.localSendFromStore.selectedDropdownItem === null
  }

  get isNextDisabled() {
    if (this.step === 2) return this.localSendFromStore.selectedDropdownItem === null
    if (this.step === 3) return !this.localEnrollmentFilters.allSize
    return false
  }

  setFocusedIndex = (index: number) => {
    this.focusedIndex = index
  }

  moveFocus = (delta: number) => {
    if (!this.currentListLength) return
    this.focusedIndex = Math.min(Math.max(this.safeIndex + delta, 0), this.currentListLength - 1)
  }

  private advanceStep = () => {
    this.step++
    this.focusedIndex = 0
  }

  goBack = () => {
    if (this.step <= 1) return
    this.step--
    this.focusedIndex = 0
  }

  handleNext = () => {
    this.advanceStep()
  }

  handleSelect = () => {
    if (this.step === 1) {
      this.localRunAutomatically = !this.localRunAutomatically
    }
  }

  selectAt = (index: number) => {
    this.setFocusedIndex(index)
    this.handleSelect()
  }

  getInboxName = () => {
    const selectedInboxItem = this.localSendFromStore.selectedDropdownItem

    if (!selectedInboxItem) return 'Skip'
    const data = selectedInboxItem.data
    const mainLabel = data?.parentLabel || selectedInboxItem.label
    const subLabel =
      data?.smart_option_label || data?.mergeField?.name || data?.number?.formatted_number
    return subLabel ? `${mainLabel}: ${subLabel}` : mainLabel
  }

  get submitMessage(): string {
    const triggerAnswer = this.localRunAutomatically ? 'Manually, Automatically' : 'Manually'
    const inboxLabel = this.getInboxName()

    if (this.isSkipped) {
      const skippedPairs: Array<{ q: string; a: string }> = [
        { q: STEP_HEADERS[0], a: 'Skip' },
        { q: STEP_HEADERS[1], a: 'Skip' },
      ]
      if (this.localRunAutomatically) {
        skippedPairs.push({ q: STEP_HEADERS[2], a: 'Skip' })
        skippedPairs.push({ q: STEP_HEADERS[3], a: 'Skip' })
      }
      return formatWidgetMessage(skippedPairs)
    }

    const pairs = [
      { q: STEP_HEADERS[0], a: triggerAnswer },
      { q: STEP_HEADERS[1], a: inboxLabel },
    ]

    if (this.localRunAutomatically) {
      const enrollmentSet = !!this.localEnrollmentFilters.allSize
      pairs.push({
        q: STEP_HEADERS[2],
        a: enrollmentSet
          ? `${this.localEnrollmentFilters.allSize} set of filters selected`
          : 'Skip',
      })
      pairs.push({
        q: STEP_HEADERS[3],
        a: RE_ENROLL_OPTIONS[this.safeIndex]?.id === 'yes' ? 'Yes' : 'No',
      })
    }

    return formatWidgetMessage(pairs)
  }

  editLocalEnrollment = () => {
    const filters = this.localEnrollmentFilters.clone()
    this.sectionStore.enrollmentStore.openModal({
      title: 'Enroll contacts',
      allowEmptyCount: true,
      filters,
      action: {
        cancel: () => Promise.resolve(true),
        confirm: async (newFilters) => {
          runInAction(() => {
            this.localEnrollmentFilters = newFilters
          })
          return true
        },
      },
    })
  }

  handleSubmit = () => {
    const message = this.submitMessage
    const shouldSetReEnroll = this.step === 4
    const reEnroll = shouldSetReEnroll ? RE_ENROLL_OPTIONS[this.safeIndex].id === 'yes' : false
    const enrollmentFilters = this.localEnrollmentFilters
    const selectedDropdownItem = this.localSendFromStore.selectedDropdownItem
    const runAutomatically = this.localRunAutomatically

    this.chatStore.sendMessage(message, () => {
      this.sectionStore.setRunAutomatically(runAutomatically)
      if (shouldSetReEnroll) {
        this.sectionStore.setAllowReEntering(reEnroll)
      }
      this.sectionStore.sendFromStore.setSelectedDropdownItem(selectedDropdownItem)
      this.sectionStore.enrollmentFilters = enrollmentFilters
    })
  }

  handleClose = () => {
    this.isSkipped = true
    const message = this.submitMessage
    this.chatStore.sendMessage(message)
  }

  reset = () => {
    this.step = 1
    this.focusedIndex = 0
    this.isSkipped = false
    this.localRunAutomatically = this.sectionStore.runAutomatically
    this.localEnrollmentFilters = EnrollmentFilters.Empty()
  }
}
